import React, {useEffect, useMemo, useState}      from 'react'
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Divider,
	FormGroup,
	IconButton,
	Paper,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs, Tooltip,
	Typography,
	useTheme,
} from '@mui/material'
import Grid                                       from '@mui/material/Unstable_Grid2' // Grid version 2
import {DEBUG, logger} from "../../util/log-utils"
import {fmtDttm}       from "../../util/prepkit-utils"
import {ArrowBack, ContentCopy, Refresh, Warning} from "@mui/icons-material"
import {doGet, doPost}                            from "../../util/do-fetch"
import {useNavigate, useParams}                   from "react-router-dom"
import LeftRightCenter                            from "../LeftRightCenter"
import getSupabase                                from "../../util/supabase-utils"


const log = logger("VideoJobProcessing", DEBUG)

const RENDER_TYPES = {
	"custom-clip": {name: "Custom", color: undefined},
	"section": {name: "Section", color: "primary"},
	"title": {name: "Title", color: "secondary"},
	"question": {name: "Question", color: "info"}
}

const STATUSES = {
	"completed": {name: "Completed", color: "success"},
	"queued": {name: "Queued", color: "warning"},
	"processing": {name: "Processing", color: "secondary"},
	"failed": {name: "Failed", color: "error"},
}

function VideoJobProcessing() {

	const navigate = useNavigate()
	const theme = useTheme()
	const {id} = useParams()
	const [videoJob, setVideoJob] = useState(null)
	const [videoJobItems, setVideoJobItems] = useState(null)
	const [tab, setTab] = useState("clips")

	const videoDuration = useMemo(() => {
		if (!videoJob?.video_job_item?.length) return ''

		const totalSeconds = videoJob.video_job_item
			.map(vi => vi.duration || 0)
			.reduce((sum, v) => sum + v, 0)

		const mins = Math.floor(totalSeconds / 60)
		const secs = totalSeconds % 60

		return `${mins}m ${secs}s`
	}, [videoJob])

	const timeCodes = useMemo(() => {
		if (!videoJobItems) return ""

		let currentTime = 0
		let questionNumber = 0
		let firstTimeCode = true

		const tc = videoJobItems
			.map(item => {
				// Only non-custom-clips get timecodes
				if (item.render_type === 'custom-clip') {
					currentTime += item.duration || 0
					return null
				}

				let minSec
				if (firstTimeCode) {
					firstTimeCode = false
					minSec = '00:00'
				}
				else {
					const minutes = Math.floor(currentTime / 60)
					const seconds = Math.floor(currentTime % 60)
					minSec = `${String(minutes)
						.padStart(2, '0')}:${String(seconds)
						.padStart(2, '0')}`
				}

				let timeCode
				if (item.render_type === 'question') {
					questionNumber++
					const questionText = item.ref_name || 'Question'
					timeCode = `${minSec} - ${questionNumber}. ${questionText}`
				}
				else if (item.render_type === 'section' || item.render_type === 'title') {
					const topicName = item.ref_name
						? item.ref_name.split('/')
							.pop()
						: 'Topic'
					timeCode = `${minSec} - ${topicName}`
				}
				else {
					timeCode = `${minSec} - Chapter`
				}

				currentTime += item.duration || 0
				return timeCode
			})
			.filter(tc => tc !== null)
			.join('\n')

		return tc

	}, [videoJobItems])

	useEffect(() => {
		fetchVideoJob()
	}, [id])

	useEffect(() => {
		if (videoJob) {
			setVideoJobItems(videoJob.video_job_item)
		}
		else {
			setVideoJobItems(null)
		}
	}, [videoJob])

	useEffect(() => {
		const subscription = subscribeToVideoJobItemChannel()
		return () => {
			console.log(`unsubscribing from video-job-channel`)
			subscription && subscription.unsubscribe()
		}
	}, [videoJob])

	const subscribeToVideoJobItemChannel = () => {
		if(!videoJob) return
		const channelName = `channel-video-job-item-${videoJob.id}`
		console.log(`subscribing to ${channelName}`)
		const supabase = getSupabase()
		const ch = supabase
			.channel(channelName)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "video_job_item",
					//filter: `video_job_id=eq.${videoJob?.id}`,
				},
				(event) => {
					console.log(`[${channelName}] : video_job_item ${event?.new?.id}`)
					if (event.eventType === "UPDATE") {
						setVideoJob(prev => ({
							...prev,
							video_job_item: prev.video_job_item.map(item =>
								item.id === event.new.id ? event.new : item
							)
						}))
					}
				}
			)
			.subscribe((status) => {
				console.log(`[${channelName}] subscription status: ${status}`)
			})
		return ch
	}

	const fetchVideoJob = async () => {
		if (id) {
			try {
				const response = await doGet(`/admin/videos/jobs/${id}`)
				const data = await response.json()
				setVideoJob(data)
			}
			catch (err) {
				log.error(err)
			}
		}
		else {
			setVideoJob(null)
		}
	}

	const downloadVideoJobItemFile = async (videoJobItem) => {
		alert("do this!")
		/*
		const url = getVideoJobItemUrl(videoJobItem)
		const signedUrl = await fetchSignedMediaUrl(url)

		const link = document.createElement('a')
		link.href = signedUrl
		link.download = videoJobItem.file_name
		link.target = "_media-item-download"
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		 */
	}

	const handleCopyTimeCodesClick = () => {
		console.log(timeCodes)
		navigator.clipboard.writeText(timeCodes)
			.then(() => {
				console.log('Time codes copied to clipboard!')
			})
			.catch(err => {
				console.error('Failed to copy time codes to clipboard:', err)
			})
	}

	const handleRetryClick = async (videoJobItemId) => {
		try {
			await doPost(`/admin/videos/jobs/${id}/items/${videoJobItemId}/retry`)
		}
		catch (err) {
			log.error(err)
			alert('Failed to retry video job item')
		}
	}

	if (!videoJobItems) return null


	return <Container maxWidth="lg">
		<Grid xs={12} container>
			<Grid xs={12}>
				<LeftRightCenter
					left={
						<Box display="flex"
						     flexDirection="row"
						     justifyContent="flex-start"
						     alignItems="center"
						>
							<h2><IconButton sx={{mb: .5}} onClick={() => navigate(`/admin/videos`)}>
								<ArrowBack/>
							</IconButton> {videoJob && videoJob.title}</h2>
							<p>{videoJob.description}</p>
						</Box>
					}
					center={
						<Tabs
							color="primary"
							value={tab}
							exclusive="true"
							onChange={(event, newTab) => setTab(newTab)}
						>
							<Tab value="clips" label="Clips"/>
							<Tab value="timeCodes" label="Time Codes"/>
						</Tabs>
					}
					right={
						<FormGroup row>
							<Button
								variant="outlined"
								onClick={handleCopyTimeCodesClick}
							>
								<ContentCopy/>Copy Time Codes
							</Button>
						</FormGroup>
					}
				/>
				<Divider/>
				{
					tab === "clips"
						? <TableContainer component={Paper}>
							<Table stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell style={{fontWeight: "bold"}}>ID</TableCell>
										<TableCell style={{fontWeight: "bold"}}>Type</TableCell>
										<TableCell style={{fontWeight: "bold"}}>Key</TableCell>
										<TableCell style={{fontWeight: "bold"}}>Started</TableCell>
										<TableCell style={{fontWeight: "bold"}}>Duration</TableCell>
										<TableCell style={{fontWeight: "bold", textAlign: "center"}}>Status</TableCell>
										<TableCell> </TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{
										videoJobItems && videoJobItems.map(i => {
											return <TableRow key={i.id}>
												<TableCell>{i.id}</TableCell>
												<TableCell>
													<Chip size={"small"}
													      color={RENDER_TYPES[i.render_type]?.color}
													      variant={"outlined"}
													      label={RENDER_TYPES[i.render_type]?.name}
													/>
												</TableCell>
												<TableCell>
													<Typography variant={"body"}
													            component={"div"}
													            maxWidth={"500px"}
													            fontWeight={600}
													            whiteSpace={"nowrap"}
													            overflow={"hidden"}
													            textOverflow={"ellipsis"}
													>
														{i.ref_name}
													</Typography>
													<Typography color={"text.secondary"} variant={"body2"}
													            fontSize={11}>{i.key}</Typography>
												</TableCell>
												<TableCell>{fmtDttm(i.started_at)}</TableCell>
												<TableCell>{i.duration}</TableCell>
												<TableCell style={{textAlign: "center"}}>

													<Chip size={"small"}
													      color={STATUSES[i.status]?.color}
													      label={
														      <Box>
															      {
																      i.status === 'processing' &&
																      <CircularProgress sx={{mr: 0.5, color: "white"}} size={10}/>
															      }
															      {STATUSES[i.status]?.name}
														      </Box>
													      }
													/>
													{
														i.status === 'failed' &&
														<IconButton onClick={(evt) => {
															evt.preventDefault()
															evt.stopPropagation()
															alert(i.error_message)
														}}>
															<Warning color="error"/>
														</IconButton>
													}
												</TableCell>

												<TableCell style={{textAlign: "center"}}>
													<Tooltip title={"Queue this item to be regenerated. Video Job" +
														" may need to be restarted"}>
														<IconButton
															size="small"
															onClick={(evt) => {
																evt.preventDefault()
																evt.stopPropagation()
																handleRetryClick(i.id)
															}}
														>
															<Refresh/>
														</IconButton>
													</Tooltip>
												</TableCell>
											</TableRow>
										})
									}
								</TableBody>
							</Table>
						</TableContainer>
						: <TableContainer component={Paper}>
							<Table>
								<TableBody>
									<TableRow>
										<TableCell>
											<textarea style={{
												display: "block",
												width: "100%",
												minHeight: "500px",
												border: `1px solid ${theme.palette.divider}`,
												padding: "10px"
											}}>
												{timeCodes}
											</textarea>
										</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</TableContainer>

				}
			</Grid>
		</Grid>
	</Container>
}

export default VideoJobProcessing
